import { useEffect, useRef, useState } from 'react'
import { ApplianceData } from '../classes/appliances/Appliance'
import { PortControl } from '../classes/ports/PortControl'
import { Port } from '../types/models'
import { PortConfig } from '../types/ports'
import { useAuth } from './useAuth'
import { useDevices } from './useDevices'

export type AppliancePort = {
  appliance: ApplianceData
  /** Port control of the appliance's first port; `undefined` when no loaded device has it. */
  portControl?: PortControl<Port, PortConfig>
}

export type AppliancePorts = {
  /** One entry per appliance, in the given order. */
  items: AppliancePort[]
  /** True while the device list is being re-fetched because some port was unresolvable. */
  syncing: boolean
}

/**
 * Resolve API-fetched appliances to the port controls of the loaded devices.
 *
 * The device list is a snapshot: it is fetched with `/auth/me` on login and page load and
 * kept in local storage, and only an explicit `updateMe()` (claim, radio config, settings)
 * refreshes it. Appliances, on the other hand, are fetched from the API on every page
 * visit. A session that logged in before a device was claimed and configured in *another*
 * session — the customer's laptop while the phone did the onboarding — therefore sees the
 * new appliances but knows nothing about their ports.
 *
 * When any appliance's port is missing, the device list is re-fetched once. Until the
 * re-fetch completes, such appliances are reported as syncing; afterwards the ones that
 * still do not resolve (the appliance outlived its device, or the device belongs to
 * another user) stay unresolved and the caller decides what to show for them.
 */
export function useAppliancePorts(appliances: ApplianceData[]): AppliancePorts {
  const { control } = useDevices()
  const { updateMe } = useAuth()
  const [syncing, setSyncing] = useState(false)
  const synced = useRef(false)

  const items = appliances.map(appliance => ({
    appliance,
    portControl: control.findPortControl(appliance.ports[0])
  }))
  const missing = items.some(item => !item.portControl)

  useEffect(() => {
    if (missing && !synced.current) {
      synced.current = true
      setSyncing(true)
      updateMe().finally(() => setSyncing(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missing])

  return { items, syncing }
}
